import { browserTracingIntegration, captureConsoleIntegration } from '@sentry/react';
import * as Sentry from '@sentry/react';
import { setFormatterLogger } from '@foresightdata/web-common-core';
import { getEnvironment, isDevelopment, isLocal } from './util/environment';
import { isPresignedUrl } from './util/presignedUrlScrub';
import {
  dropExpectedRequestFailures,
  handleChunkLoadFailures,
  stripPresignedBreadcrumbUrl,
} from './util/SentryUtils';

function initSentry() {
  if (isLocal() || isDevelopment() || !import.meta.env.VITE_SENTRY_DSN) {
    return;
  }

  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: getEnvironment(),
    beforeSend: (event, hint) => {
      const kept = dropExpectedRequestFailures(event, hint);
      return kept && handleChunkLoadFailures(kept, hint);
    },
    beforeBreadcrumb: stripPresignedBreadcrumbUrl,
    enableLogs: true, // Send all log & warn to sentry
    integrations: [
      // A presigned S3 request's span would record the signed URL; those requests go untraced.
      browserTracingIntegration({ shouldCreateSpanForRequest: (url) => !isPresignedUrl(url) }),
      captureConsoleIntegration({ levels: ['error'] }),
    ],
    release: import.meta.env.VITE_DEPLOY_VERSION,
    // Set tracesSampleRate to 1.0 to capture 100%
    // of transactions for performance monitoring.
    // We recommend adjusting this value in production
    sendDefaultPii: true,
    tracesSampleRate: 0.1,
  });
}

initSentry();
// Formatters report bad input they recovered from (an invalid FYE month, an unparseable fiscal date).
setFormatterLogger(Sentry.logger);

let appLoadSpan: Sentry.Span | undefined;

export function startAppLoadSpan() {
  if (!appLoadSpan) {
    appLoadSpan = Sentry.startInactiveSpan({ name: 'app.load', op: 'ui.load' });
  }
}

export function endAppLoadSpan() {
  appLoadSpan?.end();
  appLoadSpan = undefined;
}
